import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  LayoutDashboard, 
  Users, 
  UtensilsCrossed, 
  ClipboardList, 
  ChefHat, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  Building2,
  ArrowLeft,
  Eye,
  Key,
  History,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Headset,
  Receipt,
  Home,
  BookOpen,
  Briefcase,
  Truck
} from 'lucide-react';
import { useState } from 'react';

// Navigation items for Super Admin global view (no structure selected)
const superAdminGlobalItems = [
  { path: '/organizations', icon: Briefcase, label: 'Organizzazioni' },
  { path: '/structures', icon: Building2, label: 'Strutture' },
  { path: '/billing', icon: Receipt, label: 'Fatturazione' },
  { path: '/access-logs', icon: History, label: 'Log Accessi' },
  { path: '/settings', icon: Settings, label: 'Impostazioni' },
];

// Navigation items for Supervisore (CEO)
const supervisoreGlobalItems = [
  { path: '/structures', icon: Building2, label: 'Le Mie Strutture' },
];

// Navigation items when viewing/managing a structure (collapsible group)
const structureAssistanceItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/patients', icon: Users, label: 'Pazienti' },
  { path: '/menu', icon: UtensilsCrossed, label: 'Menu' },
  { path: '/orders', icon: ClipboardList, label: 'Comande' },
  { path: '/sheets', icon: FileText, label: 'Cartellini' },
  { path: '/kitchen', icon: ChefHat, label: 'Riepilogo' },
  { path: '/recipes', icon: BookOpen, label: 'Ricette' },
  { path: '/catering', icon: Truck, label: 'Catering' },
  { path: '/history', icon: BarChart3, label: 'Storico' },
  { path: '/admin', icon: Settings, label: 'Impostazioni', superAdminOnly: true },
];

const roleLabels = {
  super_admin: 'Super Admin',
  supervisore: 'Supervisore',
  admin_struttura: 'Admin Struttura',
  operatore: 'Operatore',
  cucina: 'Cucina'
};

export const Layout = ({ children }) => {
  const { 
    user, 
    logout, 
    structureName, 
    isSuperAdmin, 
    isAdmin,
    isViewingStructure,
    exitStructure,
    structureId
  } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [assistanceMenuOpen, setAssistanceMenuOpen] = useState(true); // Menu assistenza aperto di default

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleExitStructure = () => {
    exitStructure();
    navigate('/structures');
  };

  const isSupervisore = user?.role === 'supervisore';
  const hasStructure = structureId || (isSuperAdmin && isViewingStructure) || (isSupervisore && isViewingStructure);
  const userRole = user?.role || '';
  
  // Get global nav items based on role
  const getGlobalNavItems = () => {
    if (isSuperAdmin) return superAdminGlobalItems;
    if (isSupervisore) return supervisoreGlobalItems;
    return [];
  };
  
  // Filter structure assistance items based on role
  const getFilteredAssistanceItems = () => {
    return structureAssistanceItems.filter(item => {
      // Impostazioni nella sidebar solo per super admin
      if (item.superAdminOnly && !isSuperAdmin) return false;
      // Comande non per cucina
      if (item.path === '/orders' && userRole === 'cucina') return false;
      return true;
    });
  };

  // Render navigation item
  const renderNavItem = (item, inSubmenu = false) => (
    <NavLink
      key={item.path}
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) =>
        `flex items-center gap-3 ${inSubmenu ? 'px-4 py-2 ml-4' : 'px-4 py-3'} rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? 'bg-blue-50 text-blue-700'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`
      }
      data-testid={`nav-${item.path.slice(1) || 'dashboard'}`}
      onClick={() => setMobileMenuOpen(false)}
    >
      <item.icon className={inSubmenu ? 'w-4 h-4' : 'w-5 h-5'} />
      {item.label}
    </NavLink>
  );

  // Render collapsible menu section
  const renderAssistanceMenu = () => {
    const items = getFilteredAssistanceItems();
    
    return (
      <div className="space-y-1">
        <button
          onClick={() => setAssistanceMenuOpen(!assistanceMenuOpen)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          data-testid="assistance-menu-toggle"
        >
          <div className="flex items-center gap-3">
            <Headset className="w-5 h-5 text-amber-600" />
            <span>Gestione Struttura</span>
          </div>
          {assistanceMenuOpen ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </button>
        
        {assistanceMenuOpen && (
          <div className="ml-2 pl-2 border-l-2 border-amber-200 space-y-1">
            {items.map(item => renderNavItem(item, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 z-50">
        <div className="h-full flex flex-col bg-white border-r border-slate-200">
          {/* Logo - clickable to dashboard */}
          <Link to="/dashboard" className="h-14 flex-shrink-0 flex items-center justify-center px-3 border-b border-slate-200 hover:bg-slate-50 transition-colors">
            <img 
              src="/logo-horizontal-trimmed.png" 
              alt="ServiCare" 
              className="h-10 object-contain"
              loading="lazy"
            />
          </Link>

          {/* Super Admin Viewing Structure Banner */}
          {isViewingStructure && (
            <div className="flex-shrink-0 px-4 py-2 bg-amber-50 border-b border-amber-200">
              <div className="flex items-center gap-2 text-amber-700 mb-2">
                <Eye className="w-4 h-4" />
                <span className="text-xs font-medium">Modalità Assistenza</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800 truncate">{structureName}</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={handleExitStructure}
                data-testid="exit-structure-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna alle Strutture
              </Button>
            </div>
          )}

          {/* Normal Structure Banner (non-super admin or super admin not viewing) */}
          {structureName && !isViewingStructure && (
            <div className="flex-shrink-0 px-4 py-3 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-700" />
                <span className="text-sm font-medium text-blue-800 truncate">{structureName}</span>
              </div>
            </div>
          )}

          {/* Navigation - scrollable area */}
          <nav className="flex-1 min-h-0 px-4 py-6 space-y-1 overflow-y-auto">
            {/* Super Admin/Supervisore Global Items (when not viewing a structure) */}
            {(isSuperAdmin || isSupervisore) && !isViewingStructure && (
              <>
                {getGlobalNavItems().map(item => renderNavItem(item))}
              </>
            )}
            
            {/* Super Admin/Supervisore viewing structure: show collapsible assistance menu */}
            {(isSuperAdmin || isSupervisore) && isViewingStructure && (
              <>
                {/* Global items always visible */}
                {getGlobalNavItems().map(item => renderNavItem(item))}
                
                {/* Divider */}
                <div className="my-3 border-t border-slate-200" />
                
                {/* Collapsible assistance menu */}
                {renderAssistanceMenu()}
              </>
            )}
            
            {/* Non-Super Admin/Supervisore: show regular items */}
            {!isSuperAdmin && !isSupervisore && hasStructure && (
              <>
                {getFilteredAssistanceItems().map(item => renderNavItem(item))}
              </>
            )}
          </nav>

          {/* User Section - fixed at bottom */}
          <div className="flex-shrink-0 p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg mb-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isViewingStructure ? 'bg-amber-100' : 'bg-blue-100'}`}>
                <span className={`text-sm font-semibold ${isViewingStructure ? 'text-amber-600' : 'text-blue-700'}`}>
                  {user?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.username}</p>
                <p className="text-xs text-slate-500">
                  {roleLabels[user?.role] || user?.role}
                  {isViewingStructure && ' (Assistenza)'}
                </p>
              </div>
            </div>
            {/* Settings button for non-superadmin users (in bottom section) */}
            {!isSuperAdmin && (
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-600 hover:text-blue-700 hover:bg-blue-50"
                onClick={() => navigate('/admin')}
                data-testid="bottom-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Impostazioni
              </Button>
            )}
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header - con safe-area per PWA */}
      <header 
        className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-50 flex items-center px-2 gap-1"
        style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(4rem + env(safe-area-inset-top))' }}
      >
        {/* Menu hamburger */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
          className="shrink-0 h-10 w-10"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
        
        {/* Pulsante Home - accanto al menu */}
        <Link to="/" className="shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
            title="Torna al sito"
            data-testid="mobile-home-btn"
          >
            <Home className="w-5 h-5" />
          </Button>
        </Link>
        
        {/* Logo centrale - completo con scritta su mobile */}
        <Link to="/dashboard" className="flex items-center flex-1 justify-center min-w-0">
          <img 
            src="/logo-horizontal-trimmed.png" 
            alt="ServiCare" 
            className="h-10 object-contain"
            data-testid="mobile-logo"
            loading="lazy"
          />
        </Link>
        
        {/* Nome struttura o spazio vuoto per bilanciare */}
        <div className="shrink-0 w-20 text-right">
          {structureName ? (
            <span className={`text-[10px] leading-tight block truncate font-medium ${isViewingStructure ? 'text-amber-600' : 'text-blue-700'}`}>
              {isViewingStructure && '👁 '}{structureName}
            </span>
          ) : null}
        </div>
        
        {/* Pulsante esci da struttura (solo se in modalità assistenza) */}
        {isViewingStructure && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleExitStructure}
            className="text-amber-600 shrink-0 h-10 w-10"
            data-testid="mobile-exit-structure-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-x-0 bottom-0 bg-white z-40 overflow-y-auto flex flex-col"
          style={{ 
            top: 'calc(4rem + env(safe-area-inset-top))',
            paddingBottom: 'env(safe-area-inset-bottom)'
          }}
        >
          {/* Mobile Structure Banner when viewing */}
          {isViewingStructure && (
            <div className="flex-shrink-0 p-4 bg-amber-50 border-b border-amber-200">
              <div className="flex items-center gap-2 text-amber-700 mb-2">
                <Eye className="w-4 h-4" />
                <span className="text-sm font-medium">Modalità Assistenza</span>
              </div>
              <p className="text-sm text-amber-800 font-semibold">{structureName}</p>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2 border-amber-300 text-amber-700"
                onClick={() => { handleExitStructure(); setMobileMenuOpen(false); }}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna alle Strutture
              </Button>
            </div>
          )}
          
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {/* Super Admin/Supervisore Global Items */}
            {(isSuperAdmin || isSupervisore) && getGlobalNavItems().map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-4 rounded-lg text-base font-medium ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
            
            {/* Assistance Menu (when viewing structure or non-super admin/supervisore) */}
            {(isViewingStructure || (!isSuperAdmin && !isSupervisore && hasStructure)) && (
              <>
                {(isSuperAdmin || isSupervisore) && <div className="my-3 border-t border-slate-200" />}
                
                {/* Collapsible header for mobile */}
                {(isSuperAdmin || isSupervisore) && (
                  <button
                    onClick={() => setAssistanceMenuOpen(!assistanceMenuOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <Headset className="w-5 h-5 text-amber-600" />
                      <span>Gestione Struttura</span>
                    </div>
                    {assistanceMenuOpen ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                )}
                
                {(!isSuperAdmin && !isSupervisore || assistanceMenuOpen) && (
                  <div className={(isSuperAdmin || isSupervisore) ? "ml-4 pl-4 border-l-2 border-amber-200 space-y-1" : "space-y-1"}>
                    {getFilteredAssistanceItems().map(item => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.path === '/'}
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium ${
                            isActive
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-slate-600 hover:bg-slate-50'
                          }`
                        }
                      >
                        <item.icon className="w-5 h-5" />
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </>
            )}
          </nav>
          
          {/* Mobile footer - fixed at bottom */}
          <div className="flex-shrink-0 p-4 border-t border-slate-200 bg-white">
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-600 hover:text-red-600 py-4"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      )}

      {/* Main Content - con safe-area per PWA */}
      <main 
        className="md:pl-64 md:pt-0"
        style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top))' }}
      >
        <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};
